import { LeftOutlined, RightOutlined } from "@ant-design/icons"
import { Button } from "components/Button/Button"
import { Calendar } from "components/Calendar/Calendar"
import { DatePicker } from "components/DatePicker/DatePicker"
import { Divider } from "components/Divider/Divider"
import { Image } from "components/Image/Image"
import { LineChart } from "components/LineChart/LineChart"
import { Tag } from "components/Tag/Tag"
import { TimelineScreening } from "interface/patientType"
import { PreferredGender } from "interface/user"
import { useFlag } from "modules/flags/flags"
import { useCallback, useMemo } from "react"
import { useParams, useSearchParams } from "react-router"
import { usePatientDetails, usePatientScreeningsStats } from "services/patients"
import { useUserRole } from "services/users"
import { cn } from "styles/cn"
import { kgToLbs } from "utils"
import { asDate, dateFormatter, Dayjs, formatDate, today } from "utils/date"
import { getPronouns } from "utils/patient"
import { useNavigateToScreening } from "./useNavigateToScreening"

export const PatientSummary = ({
	latestScreening,
}: {
	latestScreening?: TimelineScreening
}) => {
	const navigate = useNavigateToScreening()
	const { userId: patientId } = useParams()
	const flags = useFlag("patientMonthlyStats")
	const currentMonth = today()
	const currentYearMonthAsString = today().format("YYYY-MM")
	const defaultSearchParams = useMemo(
		() => ({
			month: currentYearMonthAsString,
		}),
		[currentYearMonthAsString],
	)
	const [searchParams, setSearchParams] = useSearchParams(defaultSearchParams)
	const month = searchParams.get("month") ?? currentYearMonthAsString
	const monthAsDate = useMemo(() => asDate(month), [month])
	const { data: patient } = usePatientDetails(patientId)
	const { data: { stats: screenings = [], firstCaptureAt } = {} } =
		usePatientScreeningsStats({
			patientId,
			month,
		})
	const firstMonthWithScreeningDate = useMemo(
		() => asDate(firstCaptureAt).startOf("month"),
		[firstCaptureAt],
	)
	const { data: { isProviderAnon } = {} } = useUserRole()
	const calendarTooltips = useMemo(
		() =>
			screenings.reduce(
				(acc, { capturedAt }) => {
					const formattedDate = formatDate(capturedAt[0])
					acc[formattedDate] =
						`${capturedAt.length} screening${capturedAt.length > 1 ? "s" : ""} · ${formattedDate}`
					return acc
				},
				{} as Record<string, string>,
			),
		[screenings],
	)
	const lineChartData = useMemo(() => {
		if (screenings.length === 0) {
			return []
		}
		return screenings.map(({ capturedAt, weightInKilogramsForce }) => {
			const weight =
				weightInKilogramsForce.reduce((a, b) => a + b, 0) /
				weightInKilogramsForce.length
			return [capturedAt[0], kgToLbs(weight, { onlyValue: true })]
		})
	}, [screenings])
	const findScreeningIdbyDate = useCallback(
		(day: Dayjs) => {
			for (const screening of screenings) {
				const i = screening.capturedAt.findIndex((d) =>
					asDate(d).isSame(day, "day"),
				)
				if (i !== -1) {
					return screening.ids[i]
				}
			}

			return null
		},
		[screenings],
	)
	const fullName =
		!isProviderAnon && !!patient?.fullName ? patient.fullName : "the patient"
	const insights = useMemo(() => {
		if (!patient) {
			return ""
		}
		const { self, possessive } = getPronouns(
			(patient.preferredGender || patient.biologicalSex) as PreferredGender,
		)
		const monthDaysCount = currentMonth.isSame(monthAsDate, "month")
			? currentMonth.date()
			: monthAsDate.daysInMonth()
		const { weightInKilogramsForce = 0, weightIsUnstable } =
			latestScreening ?? {}
		const weightStr = weightInKilogramsForce
			? `${possessive} latest weight measurement was ${weightIsUnstable ? "~" : ""}${kgToLbs(weightInKilogramsForce)}.`
			: ""
		return `So far, ${fullName} has screened ${self} ${lineChartData.length || 0} out of ${monthDaysCount} days ${weightStr ? `and ${weightStr}` : ""}`
	}, [
		currentMonth,
		fullName,
		latestScreening,
		lineChartData.length,
		monthAsDate,
		patient,
	])
	const handleChangeMonth = useCallback(
		(newMonth: Dayjs) =>
			setSearchParams({
				...Object.fromEntries(searchParams.entries()),
				month: `${newMonth.format("YYYY-MM")}`,
			}),
		[searchParams, setSearchParams],
	)

	if (!patient) {
		return null
	}

	return (
		<div
			role="region"
			aria-label="Patient summary"
			className={cn(
				"grid grid-cols-[1fr_0] gap-y-6",
				"@min-4xl:grid-cols-[1fr_auto_1fr_0]",
				"@min-7xl:grid-cols-[max-content_auto_max-content_auto_1fr]",
				"rounded-[20px] border border-mint-20 p-6 shadow-secondary",
			)}
		>
			<div className="flex min-w-85 flex-col gap-5 @min-7xl:w-85">
				<h3 className="flex items-center justify-between gap-2 font-headings text-xl">
					Latest screening
					<Tag variant="filled">
						{latestScreening
							? dateFormatter(latestScreening.capturedAt, { hasYear: false })
							: null}
					</Tag>
				</h3>
				{latestScreening?.imageThumbS3Key && (
					<button
						className="size-full rounded-2xl"
						aria-label={`Navigate to latest screening at ${dateFormatter(
							latestScreening.capturedAt,
							{
								hasYear: false,
							},
						)}`}
						onClick={() => navigate(latestScreening.id)}
					>
						<Image
							src={latestScreening.imageThumbS3Key}
							rootClassName="size-full"
							className="rounded-2xl object-cover"
						/>
					</button>
				)}
			</div>

			<Divider vertical className="mx-6 h-full" />

			<div className="flex min-w-82 flex-col gap-5 @min-7xl:w-82">
				<h3 className="flex items-center justify-between gap-2 font-headings text-xl">
					Screenings
					<div className="flex flex-row gap-1">
						<Button
							type="text"
							icon={<LeftOutlined aria-hidden />}
							aria-label="Go to previous month"
							disabled={monthAsDate
								.subtract(1, "month")
								.isBefore(firstMonthWithScreeningDate, "month")}
							onClick={() =>
								handleChangeMonth(monthAsDate.subtract(1, "month"))
							}
						/>
						<DatePicker.Month
							value={monthAsDate}
							minDate={firstMonthWithScreeningDate}
							maxDate={currentMonth}
							className="w-40"
							aria-label="Select month"
							onChange={(date) => date && handleChangeMonth(date)}
						/>
						<Button
							type="text"
							icon={<RightOutlined aria-hidden />}
							aria-label="Go to next month"
							disabled={monthAsDate
								.add(1, "month")
								.isSame(currentMonth.add(1, "month"), "month")}
							onClick={() => handleChangeMonth(monthAsDate.add(1, "month"))}
						/>
					</div>
				</h3>
				<div className="text-lg text-navy-80">{insights}</div>
				<Calendar
					key={month}
					defaultValue={monthAsDate}
					tooltips={calendarTooltips}
					validRange={[firstMonthWithScreeningDate, currentMonth]}
					onSelect={(date) => {
						const screeningId = findScreeningIdbyDate(date)
						if (screeningId) {
							navigate(screeningId)
						}
					}}
				/>
			</div>

			{flags.patientMonthlyStats.enabled && (
				<>
					<Divider vertical className="mx-6 h-full" />

					<div className="relative flex max-w-full min-w-80 flex-col @max-7xl:col-span-full">
						<h3 className="font-headings text-xl">Weight</h3>
						<LineChart
							key={month}
							data={lineChartData}
							className="min-h-96"
							aria-label="Weight chart"
							role="img"
						/>
					</div>
				</>
			)}
		</div>
	)
}
